/* WFS Component */

import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../App";
import { BaseViewContext } from "../../views/Base";
import { useLeafletContext } from "@react-leaflet/core";
import { useLocation, useParams } from "react-router";
import { useMapEvents } from "react-leaflet";
import GeoService from "../../services/GeoService";
import FieldsService from "../../services/Fields";
import SeasonService from "../../services/Season";
import * as L from 'leaflet';
import { isJSON } from "../../utilities/check";
import * as turf from '@turf/turf'

/* This will get GeoJSON data from GeoServer based on the extent the user is viewing currently */
function OGRWFS(props) {
    const {selected_user_asset, setSelectedUserAsset, setSelectedIndexOverlay, globalSelectedRegion, setGlobalSelectedRegion, activeSeasons, setInvestigatePolygon} = useContext(GlobalContext);
    const {setViewLoading} = useContext(BaseViewContext)
    const context = useLeafletContext()
    const wfs_ref = useRef()
    const [wfs_data, setWFSData] = useState(null);
    const [map_moved, setMapMoved] = useState(null);
  
    const location = useLocation();
    const {id} = useParams();

    const focusOnField = (field_id) => {
      wfs_ref.current.eachLayer(function (layer) {  
        if(layer.feature.properties.id === field_id) {    
          const bounds = layer.getBounds();
          props.map_ref.current.fitBounds(bounds);
          layer.setStyle({color: "red", fillColor: "gray"}) 
        }
      });
    }
  
    useMapEvents({
      moveend: (event) => {
        setMapMoved(event); // ping the useEffect below after pan, flyTo, fitBounds, and similar map moves
      },
      zoomend: (event) => {
        setMapMoved(event);
      },
    })
  
    useEffect(() => {
      const fetchWFS = async (extent) => {
        setViewLoading(true);
        let data = "";

        if (props.layer_name === 'regions') {
          data = await GeoService.getRegions(extent, props.map_ref.current.getZoom()) // if it's coming from Regions
          console.log("DATA REGIONS: ", data);
        }else {
          data = await GeoService.getWFS(extent, props.layer_name, props.org_id) //Anywhere else
        }
        
        // Filter fields based on active seasons
        if (props.layer_name === 'fields') {
          if (activeSeasons.size > 0) {
            const filteredData = {
              ...data,
              features: data.features?.filter(feature => {
                const seasonId = feature.properties.season_id;
                return seasonId && activeSeasons.has(seasonId);
              }) || []
            };
            setWFSData(filteredData);
          } else {
            setWFSData({ ...data, features: [] });
          }
        } else {
          setWFSData(data);
        }
        setViewLoading(false);
      }
      
      if(props.map_ref) { 
        console.log("MAP IS READY TO PULL CC: ", props.map_ref)
        const bounds = props.map_ref?.current.getBounds();
        let ne = bounds.getNorthEast(); // Coords of the northeast corner
        let sw = bounds.getSouthWest(); // Coords of the southwest corner
        const extent = [sw.lng, sw.lat, ne.lng, ne.lat];
        fetchWFS(extent);
      }
      
    }, [props.map_ref, map_moved, props.layer_name, props.org_id, setViewLoading, activeSeasons])
  
  
    useEffect(() => {
      if(selected_user_asset && wfs_ref?.current) {
        focusOnField(selected_user_asset.id)
      }
    }, [selected_user_asset])
  
  
    const onFeatureClick = async (e) => {
      const feature = e.target;
      if(props.layer_name !== 'regions') {
        const fieldId = feature.feature?.properties?.id;
        const seasonId = feature.feature?.properties?.season_id;
        if (selected_user_asset?.id === fieldId) { //if it is the second click on the selected user asset
          //feature.resetStyle();

          feature.setStyle({
            color: "blue",
            fillColor: "green"
          })
    
          props.map_ref.current.zoomOut(2);
          setSelectedUserAsset(null);
          
          // If on investigate screen, also clear investigatePolygon
          if (location.pathname === '/investigate') {
            setInvestigatePolygon(null);
          }
          //setSelectedIndexOverlay(null);
        } else {
          //feature.resetStyle();
          
          feature.setStyle({
            color: "red",
            fillColor: "blue",
            stroke: "purple"
          })
          
          const bounds = feature.getBounds();
          props.map_ref.current.fitBounds(bounds);
          // For fields layer, fetch full field meta and attach current_season if missing
          if (props.layer_name === 'fields') {
            try {
              setViewLoading(true);
              let fullField = await FieldsService.getFieldMeta(fieldId);
              if (!fullField?.current_season && seasonId) {
                try {
                  const season = await SeasonService.getSeasonById(seasonId);
                  fullField = { ...fullField, current_season: season };
                } catch (e2) {
                  console.warn('Failed to fetch season for clicked field', fieldId, seasonId, e2);
                }
              }
              setSelectedUserAsset(fullField);
            } catch (fetchErr) {
              console.error('Failed to fetch field meta, falling back to minimal selection', fetchErr);
              setSelectedUserAsset({...feature.feature.properties, geometry: feature.feature.geometry});
            } finally {
              setViewLoading(false);
            }
          } else {
            setSelectedUserAsset({...feature.feature.properties, geometry: feature.feature.geometry});
          }
          
          // If on investigate screen, also set investigatePolygon
          if (location.pathname === '/investigate') {
            setInvestigatePolygon(feature.feature);
          }
        
        }
      } else if (props.layer_name === 'regions') {
        const bounds = feature.getBounds();
        props.map_ref.current.fitBounds(bounds);
        setGlobalSelectedRegion(feature.feature)
        //TODO - Needs a "reclick to deselect" feature
        // if(globalSelectedRegion?.properties?.key === feature.feature.properties.key) { // if this feature is already selected, deselect it.
        //   setGlobalSelectedRegion({})
        // } else {
        //   console.log("LDFKNDF", feature.feature.properties.key)
        //   setGlobalSelectedRegion(feature.feature)
        // }
      }
    }
  
    useEffect(() => {

      if(!isJSON(wfs_data)) { // if the wfs data is anything but JSON, then halt
        console.log("HALTED")
        return
      }

      wfs_ref.current = new L.geoJSON(wfs_data, {
        minZoom: props.minZoom,
        zIndex: 1400,
        onEachFeature: (feature, layer) => {
          layer.on({
            click: onFeatureClick,
            mouseover: (e) => {
              const layer = e.target;


              if(props.layer_name !== 'regions') {
                return
              }
             
              layer.setStyle({
                color: "black",
                fillColor: "transparent",
                weight: 1.5,
                dashArray: '3 3',
                dashOffset: '0',
              })
            },
            mouseout: (e) => {
              const layer = e.target;


              if(props.layer_name !== 'regions') {
                return
              }
              
              layer.setStyle({
                color: "gray",
                fillColor: "transparent",
                weight: 1,
                dashArray: '3 3',
                dashOffset: '0',
              })
            }
          });

          let feature_geom_area = "";
          try { feature_geom_area = (turf.area(feature) * 0.0001).toPrecision(4); } catch (error) { console.error("Invalid feature geometry", error); }
          let layer_tooltip = "";
          if(props.layer_name === 'fields') {
            layer_tooltip = feature.properties.name;
          } else if(props.layer_name !== 'regions') {
            layer_tooltip = (feature.properties.name || "") + " " + feature_geom_area + " ha"
          } else {
            layer_tooltip = feature.properties.name;
          }

          layer.bindTooltip( layer_tooltip, {
            permanent: false,
            direction: 'center', // Direction can be 'center', 'top', 'bottom', 'left', or 'right'
            offset: [0, 0],
            className: 'feature-label'
          });
        },
        style: (feature) => {

          let _style = {
            weight: 2,            // stroke width
            fillOpacity: 0.7      // fill opacity};
          } 

          if(props.layer_name === 'fields') { // if showing fields
            _style = {
              color: feature.properties?.color || "#F44336",
              fillColor: feature.properties?.color || "#FFCDD2",
              weight: 2,
              fillOpacity: 0.7
            }
          }

          if(props.layer_name === 'regions') { // if showing regions
            _style = {
              color: "gray",
              fillColor: "transparent",
              weight: 1,
              dashArray: '3 3',
              dashOffset: '0',
            }
          }

          return _style;
        }
      })
  
      props.map_ref?.current.addLayer(wfs_ref.current)

      if(props.layer_name === 'regions') {
        wfs_ref.current.bringToFront();
      }
  
      return () => {
        props.map_ref?.current?.removeLayer(wfs_ref.current)
      }
    }, [props.map_ref, selected_user_asset, setSelectedIndexOverlay, setSelectedUserAsset, wfs_data])

    return null
  }

  export default OGRWFS;
